// localStorage helpers. Every read and write is wrapped: private browsing,
// a locked-down browser profile, or a disabled storage API can all make
// localStorage throw, and losing a remembered preference is fine — failing
// to join a call because of it is not.

import { ROOT_HANDLE } from "@breeze/core";

const NAME_KEY = "breeze:name";
const CAMERA_KEY = "breeze:camera-enabled";
const MIC_KEY = "breeze:mic-enabled";
// v2 since 2026-10-04 (Adam: "blur bg should be unchecked by default"): every
// saved "on" from before then is dropped once, so blur starts off.
const BLUR_KEY = "breeze:blur:v2";
const DEVICE_KEY = "breeze:device-id";

function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // See the file comment: silently dropped on purpose.
  }
}

function remove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Same as above.
  }
}

export function getRememberedName(): string {
  return read(NAME_KEY) ?? "";
}

export function setRememberedName(name: string): void {
  write(NAME_KEY, name);
}

// An audio-only call (a link with `?audio=1`) starts with the camera off
// for this page load only, without changing the remembered choice.
let cameraOverride: boolean | null = null;

export function getCameraEnabled(): boolean {
  return cameraOverride ?? read(CAMERA_KEY) !== "false";
}

export function setCameraEnabled(on: boolean): void {
  cameraOverride = null;
  write(CAMERA_KEY, String(on));
}

/** Whether this device blurs its camera's background. Off until chosen. */
export function getBlurEnabled(): boolean {
  return read(BLUR_KEY) === "true";
}

export function setBlurEnabled(on: boolean): void {
  write(BLUR_KEY, String(on));
}

/** Reads the call flags a link can carry and takes them out of the address
 * bar: `audio=1` starts with the camera off (an audio call), `listen=1`
 * starts with the camera off and the mic muted (Listen in on an Auto Breeze
 * call; the mic button unmutes), `go=1` joins
 * at once with the remembered name, skipping the join screen (an instant
 * call someone tapped to place or answer), and `move=1` makes that join
 * take this account's seat over from another device. `return=<url>` names
 * the page to go back to on leaving (Seamless passes its own Calls view;
 * only https pages on adammirmina.com and this origin are taken). */
export function captureCallFlagsFromUrl(): { readonly go: boolean; readonly move: boolean } {
  try {
    const url = new URL(window.location.href);
    const audio = url.searchParams.get("audio") === "1";
    const listen = url.searchParams.get("listen") === "1";
    const go = url.searchParams.get("go") === "1";
    const move = url.searchParams.get("move") === "1";
    const back = url.searchParams.get("return");
    if (back !== null) returnUrl = safeReturnUrl(back);
    if (audio || listen) cameraOverride = false;
    if (listen) micOverride = false;
    if (!audio && !listen && !go && !move && back === null) return { go: false, move: false };
    url.searchParams.delete("audio");
    url.searchParams.delete("listen");
    url.searchParams.delete("go");
    url.searchParams.delete("move");
    url.searchParams.delete("return");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
    return { go, move };
  } catch {
    return { go: false, move: false };
  }
}

let returnUrl: string | null = null;

function safeReturnUrl(raw: string): string | null {
  try {
    const u = new URL(raw, window.location.origin);
    const host = u.hostname.toLowerCase();
    const ours = u.origin === window.location.origin || host === "adammirmina.com" || host.endsWith(".adammirmina.com");
    return ours && (u.protocol === "https:" || u.origin === window.location.origin) ? u.href : null;
  } catch {
    return null;
  }
}

/** Where leaving a call goes, when the link that opened it said. */
export function getReturnUrl(): string | null {
  return returnUrl;
}

// Listening in (`?listen=1`) starts muted for this page load only.
let micOverride: boolean | null = null;

export function getMicEnabled(): boolean {
  return micOverride ?? read(MIC_KEY) !== "false";
}

export function setMicEnabled(on: boolean): void {
  micOverride = null;
  write(MIC_KEY, String(on));
}

/** A stable per-browser id, so a reconnect from this same device is
 * recognized by the Room DO rather than treated as a brand-new person
 * (packages/core's JoinMessage.deviceId). */
export function getOrCreateDeviceId(): string {
  const existing = read(DEVICE_KEY);
  if (existing) return existing;
  const id = crypto.randomUUID();
  write(DEVICE_KEY, id);
  return id;
}

function rejoinKey(roomKey: string): string {
  return `breeze:rejoin:${roomKey}`;
}

/** The rejoin token the Room DO handed us for this specific room, so a page
 * reload or a dropped connection can rejoin as the same participant instead
 * of knocking again. */
export function getRejoinToken(roomKey: string): string | null {
  return read(rejoinKey(roomKey));
}

export function setRejoinToken(roomKey: string, token: string): void {
  write(rejoinKey(roomKey), token);
}

export function clearRejoinToken(roomKey: string): void {
  remove(rejoinKey(roomKey));
}

const USER_KEY = "breeze:user-key";

/** This browser's Breeze user key, if its owner has handed it one (see
 * worker/src/userKeys.ts). It is what makes the owner the host of their own
 * room. Not user data: losing it only means opening the key link again. */
export function getUserKey(): string | null {
  return read(USER_KEY);
}

/** Stores the key a sign-in or sign-up handed back. */
export function setUserKey(key: string): void {
  write(USER_KEY, key);
}

/** Signing out: this browser stops being anyone's. */
export function clearUserKey(): void {
  remove(USER_KEY);
  // The cookie copy goes too, or the next load would sign this browser
  // straight back in.
  void fetch("/api/auth/forget", { method: "POST", keepalive: true }).catch(() => {});
}

const KEY_SHAPE = /^bk1\.[a-z0-9]+\.[a-z0-9-]+\.[A-Za-z0-9_-]+$/;

/** Keeps this browser's key in an HttpOnly cookie as well, so clearing or
 * expiring localStorage (Safari does after a week away) does not sign Adam
 * out. Quiet on every failure: the stored key still works without it. */
export function rememberUserKey(): void {
  const key = getUserKey();
  if (!key) return;
  void fetch("/api/auth/remember", { method: "POST", headers: { authorization: `Bearer ${key}` } }).catch(() => {});
}

/** When this browser has no key of its own, asks the Worker for the one in
 * its cookie and stores it. Waits at most two seconds, so a slow answer never
 * holds the page. */
export async function restoreUserKey(): Promise<void> {
  if (getUserKey()) return;
  try {
    const res = await fetch("/api/auth/restore", { signal: AbortSignal.timeout(2000) });
    if (res.status !== 200) return;
    const body = (await res.json()) as { key?: unknown };
    if (typeof body.key === "string" && KEY_SHAPE.test(body.key)) write(USER_KEY, body.key);
  } catch {
    // Offline, slow, or no cookie: the sign-in page is still there.
  }
}

/** Where an account lands when there is no page to go back to: its own
 * room's pre-join page. Meet has no account home (Adam, 2026-10-05: "the
 * only surface of meet.am will be that particular domain"); the call list
 * lives in Seamless Calls. The bare domain for the root account, `/<handle>`
 * for the others, and the bare domain for anyone signed out. */
export function ownRoomPath(): string {
  const handle = getKeyHandle();
  return !handle || handle.toLowerCase() === ROOT_HANDLE ? "/" : `/${handle}`;
}

/** The handle a stored key names, without asking the server. */
export function getKeyHandle(): string | null {
  return getUserKey()?.split(".")[2] ?? null;
}

/** Moves a key handed over as `?k=<key>` into storage and takes it back
 * out of the address bar, so it is not left in history or a shared link. */
export function captureUserKeyFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    const key = url.searchParams.get("k");
    if (!key) return;
    if (/^bk1\.[a-z0-9]+\.[a-z0-9-]+\.[A-Za-z0-9_-]+$/.test(key)) write(USER_KEY, key);
    url.searchParams.delete("k");
    window.history.replaceState(null, "", url.pathname + url.search + url.hash);
  } catch {
    // No URL API or no history: nothing to capture.
  }
}

// ---- In-call panel (private notes) -------------------------------------------
// The private notes live on the Room under a key only this browser can present
// (its token, or the signed-in account). The copy kept here lets a reload show
// them before the Room answers, and survives the Room being unreachable.

const PANEL_TOKEN_KEY = "breeze:panel-token";
let memoryPanelToken: string | null = null;

/** The secret this browser presents to get its own private notes back. Random,
 * never shown, never sent to anyone else. Falls back to memory when storage
 * is unavailable, which only costs the notes surviving a reload. */
export function getPanelToken(): string {
  const stored = read(PANEL_TOKEN_KEY);
  if (stored && /^[A-Za-z0-9_-]{22,64}$/.test(stored)) return stored;
  if (memoryPanelToken) return memoryPanelToken;
  const bytes = new Uint8Array(24);
  crypto.getRandomValues(bytes);
  const token = btoa(String.fromCharCode(...bytes)).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
  memoryPanelToken = token;
  write(PANEL_TOKEN_KEY, token);
  return token;
}

export function getPanelLocal(roomKey: string): string | null {
  return read(`breeze:panel:${roomKey}`);
}

export function setPanelLocal(roomKey: string, json: string): void {
  write(`breeze:panel:${roomKey}`, json);
}

export function getPanelPrev(roomKey: string): string | null {
  return read(`breeze:panel:${roomKey}:prev`);
}

export function setPanelPrev(roomKey: string, json: string): void {
  write(`breeze:panel:${roomKey}:prev`, json);
}

export function clearPanelPrev(roomKey: string): void {
  remove(`breeze:panel:${roomKey}:prev`);
}
